import type { Product } from '@packages/gql/generated/shopping/graphql';
import {
  LazySampleOrder,
  SampleOrderErrorFallback,
  SampleOrderSuspenseFallback,
} from '@packages/sample-order';
import type { LinkButtonProps } from '@packages/shared';
import { Dialog, ErrorBoundary, LinkButton, Stack } from '@packages/shared';
import { Fabricsamples32 } from '@packages/themes/icons';
import type { FC } from 'react';
import { Suspense, useState } from 'react';
import { useIntl } from 'react-intl';
import { messages } from './messages';

export type SampleOrderCtAProps = Pick<Product, 'akl' | 'sku' | 'sampleOrderType'> &
  LinkButtonProps;

/**
 * This component is used to display a clickable call to action (CtA) link,
 * which opens a dialog that contains the `SampleOrder`.
 * */
export const SampleOrderCtA: FC<SampleOrderCtAProps> = ({
  sku,
  akl,
  sampleOrderType,
  sx,
  ...rest
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { formatMessage } = useIntl();

  return (
    <>
      <LinkButton
        {...rest}
        sx={{ ...sx, cursor: 'pointer' }}
        disableUnderlined
        onClick={() => {
          setIsDialogOpen(true);
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Fabricsamples32
            sx={{
              fontSize: '1.5rem',
            }}
          />
          {formatMessage(
            sampleOrderType === 'WOOD'
              ? messages.sampleOrderCtALinkWoodPattern
              : messages.sampleOrderCtALinkFabricPattern,
          )}
        </Stack>
      </LinkButton>
      <Dialog
        size="xl"
        open={isDialogOpen}
        handleClose={() => setIsDialogOpen(false)}
        title={formatMessage(messages.modalHeadline)}
        dialogContentSxProps={{ height: '100%', overflow: 'hidden', flex: '1', display: 'flex' }}
        dialogContent={
          <ErrorBoundary fallback={(reset) => <SampleOrderErrorFallback onCloseHandler={reset} />}>
            <Suspense fallback={<SampleOrderSuspenseFallback />}>
              <LazySampleOrder sku={sku} akl={akl} abortHandler={() => setIsDialogOpen(false)} />
            </Suspense>
          </ErrorBoundary>
        }
      />
    </>
  );
};
